import { safeGetAttributeModel } from '../../../../../../Model/avenyModelUtils';
import { AvenyCollectionMap } from '../../../../../../Model/ModelContext/ModelContext';
import { IClientBusinessModel } from '../../../../../../Types/ClientModelTypes';
import {
  IFormData,
  IFieldValidations,
} from '../../../../_Common/Components/BusinessCollectionComponents/BusinessDataForm/Utils/BusinessDataFormUtils';
import { StatistikrapporteringAldersindelningQuery_db_BOAldersindelning_All } from '../Queries/_QueryTypes/StatistikrapporteringAldersindelningQuery';
import { StatistikrapporteringQuery_node_TEDBOAktivitet } from '../Queries/_QueryTypes/StatistikrapporteringQuery';
import { aktuellaAldrar } from './Utils';

const statValue = (attrValue: string | undefined) => {
  return parseInt(attrValue || '', 10) || 0;
};

const isValidStatValue = (
  errorMessages: IFieldValidations,
  attr: string,
  displayLabel: string,
  attrValue: string | undefined
): number => {
  const v = statValue(attrValue);

  if (v < 0) {
    errorMessages.push({
      fieldName: attr,
      message: displayLabel + ' får inte vara mindre än 0',
      type: 'Error',
    });
  } else if (v > 1000) {
    errorMessages.push({
      fieldName: attr,
      message: 'Varning: Stort antal',
      type: 'Warning',
    });
  }
  return v;
};

const isValidAttrStatValue = (
  errorMessages: IFieldValidations,
  attr: string,
  attrValue: string | undefined,
  statistikModel: IClientBusinessModel,
  avenyCollectionMap: AvenyCollectionMap
): number => {
  const attrInfo = safeGetAttributeModel(
    statistikModel,
    attr,
    avenyCollectionMap
  );
  return isValidStatValue(
    errorMessages,
    attr,
    attrInfo?.DisplayLabel || 'Antal',
    attrValue
  );
};

const isValidFormStatValue = (
  errorMessages: IFieldValidations,
  attr: string,
  data: IFormData,
  statistikModel: IClientBusinessModel,
  avenyCollectionMap: AvenyCollectionMap
): number => {
  return isValidAttrStatValue(
    errorMessages,
    attr,
    data[attr],
    statistikModel,
    avenyCollectionMap
  );
};

const isValidDigitalStatValue = (
  errorMessages: IFieldValidations,
  attr: string,
  data: StatistikrapporteringQuery_node_TEDBOAktivitet,
  statistikModel: IClientBusinessModel,
  avenyCollectionMap: AvenyCollectionMap
): number => {
  return isValidAttrStatValue(
    errorMessages,
    'DigitalaVisningar.' + attr,
    (data._Associations?.DigitalaVisningar &&
      (data._Associations?.DigitalaVisningar as unknown as Record<string, string | null | undefined>)[attr]) ?? undefined,
    statistikModel,
    avenyCollectionMap
  );
};

const isValidAlderStatValue = (
  allaGiltigaAldrar: StatistikrapporteringAldersindelningQuery_db_BOAldersindelning_All[],
  errorMessages: IFieldValidations,
  id: string,
  data: IFormData
): number => {
  let alder = allaGiltigaAldrar.find(a => a.id === id);
  return isValidStatValue(
    errorMessages,
    id,
    (alder && alder.BENAMNING) || 'Antal',
    data.aldrar && data.aldrar[id]
  );
};

export const validateStandardStatistik = (
  data: IFormData,
  statistikModel: IClientBusinessModel,
  avenyCollectionMap: AvenyCollectionMap
): IFieldValidations => {
  const errorMessages: IFieldValidations = [];
  isValidFormStatValue(
    errorMessages,
    'ANTAL_BESOKARE',
    data,
    statistikModel,
    avenyCollectionMap
  );
  isValidFormStatValue(
    errorMessages,
    'ANTAL_BARN',
    data,
    statistikModel,
    avenyCollectionMap
  );
  if (data.ANTAL_BARN && data.ANTAL_BARN > (data.ANTAL_BESOKARE || 0)) {
    errorMessages.push({
      fieldName: 'ANTAL_BARN',
      message: 'Antal barn får inte överstiga antal besökare',
      type: 'Error',
    });
  }
  isValidFormStatValue(
    errorMessages,
    'ANTAL_KOMMUNIONER',
    data,
    statistikModel,
    avenyCollectionMap
  );
  if (
    data.ANTAL_KOMMUNIONER &&
    data.ANTAL_KOMMUNIONER > (data.ANTAL_BESOKARE || 0)
  ) {
    errorMessages.push({
      fieldName: 'ANTAL_KOMMUNIONER',
      message: 'Antal kommunioner får inte överstiga antal besökare',
      type: 'Error',
    });
  }
  return errorMessages;
};

export const validateDigitalStatistik = (
  data: StatistikrapporteringQuery_node_TEDBOAktivitet,
  statistikModel: IClientBusinessModel,
  avenyCollectionMap: AvenyCollectionMap
): IFieldValidations => {
  const errorMessages: IFieldValidations = [];
  let tot = isValidDigitalStatValue(
    errorMessages,
    'VISNINGAR_TOTAL',
    data,
    statistikModel,
    avenyCollectionMap
  );
  let sum =
    isValidDigitalStatValue(
      errorMessages,
      'VISNINGAR_FACEBOOK',
      data,
      statistikModel,
      avenyCollectionMap
    ) +
    isValidDigitalStatValue(
      errorMessages,
      'VISNINGAR_YOUTUBE',
      data,
      statistikModel,
      avenyCollectionMap
    ) +
    isValidDigitalStatValue(
      errorMessages,
      'VISNINGAR_VIMEO',
      data,
      statistikModel,
      avenyCollectionMap
    ) +
    isValidDigitalStatValue(
      errorMessages,
      'VISNINGAR_OVRIG',
      data,
      statistikModel,
      avenyCollectionMap
    );
  if (sum != tot) {
    errorMessages.push({
      fieldName: 'DigitalaVisningar.VISNINGAR_TOTAL',
      message: `Summan av antalet visningar (${sum}) stämmer inte med rapporterat total antal`,
      type: 'Error',
    });
  }
  return errorMessages;
};

export const validateAlderStatistik = (
  data: IFormData,
  allaGiltigaAldrar: StatistikrapporteringAldersindelningQuery_db_BOAldersindelning_All[],
  nodeData: StatistikrapporteringQuery_node_TEDBOAktivitet | null | undefined,
  statistikModel: IClientBusinessModel,
  avenyCollectionMap: AvenyCollectionMap
): IFieldValidations => {
  const errorMessages: IFieldValidations = [];
  isValidFormStatValue(
    errorMessages,
    'ANTAL_BESOKARE',
    data,
    statistikModel,
    avenyCollectionMap
  );
  if (data.aldrar)
    for (let key in data.aldrar) {
      if (key) {
        isValidAlderStatValue(allaGiltigaAldrar, errorMessages, key, data);
      }
    }
  let aldrar = aktuellaAldrar(data, nodeData);
  let sum = 0;
  if (aldrar) {
    for (let key in aldrar) {
      sum += aldrar[key];
    }
  }
  if (sum != (data.ANTAL_BESOKARE || 0)) {
    errorMessages.push({
      fieldName: 'ANTAL_BESOKARE',
      message: `Åldersindelningen (${sum}) stämmer inte med rapporterat antal besökare`,
      type: 'Error',
    });
  }
  return errorMessages;
};
